import React, { useState, useEffect } from 'react';
import { readerApi, familyApi, reportApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import ReaderSelector from '../components/reporting/ReaderSelector';
import ReadingDetailsForm from '../components/reporting/ReadingDetailsForm';
import { hasSimplifiedSetup } from '../utils/userSetup';
import { localDateOnly } from '../utils/localDate';
import './ManageReportsPage.css';

const ManageReportsPage = () => {
  const { preferences } = useAuth();
  const competitiveMode = Boolean(preferences?.competitiveMode);
  const [readers, setReaders] = useState([]);
  const [families, setFamilies] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingReports, setLoadingReports] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Filter state
  const [selectedReaders, setSelectedReaders] = useState([]);
  const [readerMode, setReaderMode] = useState('all'); // 'all', 'primary', 'family', 'specific'
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedBook, setSelectedBook] = useState('');
  const [selectedTestament, setSelectedTestament] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalReports, setTotalReports] = useState(0);
  const reportsPerPage = 100;
  const [editingReport, setEditingReport] = useState(null);
  const [, setShowReadingDetails] = useState({});
  const [selectedReports, setSelectedReports] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    // Clear selections when filters change
    setSelectedReports([]);
    setSelectAll(false);
    loadReports();
  }, [selectedReaders, startDate, endDate, selectedBook, selectedTestament, currentPage]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [readersRes, familiesRes] = await Promise.all([
        readerApi.getReaders(),
        familyApi.getFamilies()
      ]);

      const readersData = readersRes.data.readers || [];
      const familiesData = familiesRes.data.families || [];
      
      setReaders(readersData);
      setFamilies(familiesData);

      // For users with simplified setup, default to primary reader mode
      const isSimplifiedSetup = hasSimplifiedSetup(readersData, familiesData);
      if (isSimplifiedSetup) {
        const primaryReader = readersData.find(r => r.isPrimary);
        if (primaryReader) {
          setReaderMode('primary');
          setSelectedReaders([primaryReader]);
        }
      }

      setError(null);
    } catch (err) {
      console.error('Failed to load initial data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadReports = async () => {
    try {
      setLoadingReports(true);
      
      const params = {
        page: currentPage,
        limit: reportsPerPage
      };

      // Add filters
      if (selectedReaders.length === 1) {
        params.readerId = selectedReaders[0].id;
      } else if (selectedReaders.length > 1) {
        params.readerIds = selectedReaders.map(reader => reader.id).join(',');
      }
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (selectedBook) params.book = selectedBook;
      if (selectedTestament) params.testament = selectedTestament;

      const response = await reportApi.getReports(params);
      setReports(response.data.reports || []);
      setCurrentPage(response.data.pagination?.currentPage || 1);
      setTotalPages(response.data.pagination?.totalPages || 1);
      setTotalReports(response.data.pagination?.totalReports || 0);
      setError(null);
    } catch (err) {
      console.error('Failed to load reports:', err);
      setError('Failed to load reports. Please try again.');
    } finally {
      setLoadingReports(false);
    }
  };

  const handleReaderModeChange = (mode) => {
    setReaderMode(mode);
    setSelectedReaders([]);
    setSelectedFamily(null);
    setCurrentPage(1);

    if (mode === 'primary') {
      const primaryReader = readers.find(r => r.isPrimary);
      if (primaryReader) {
        setSelectedReaders([primaryReader]);
      }
    } else if (mode === 'all') {
      setSelectedReaders([]);
    }
  };

  const handleFamilySelect = (family) => {
    setSelectedFamily(family);
    setSelectedReaders(family?.readers || []);
    setCurrentPage(1);
  };

  const handleReaderToggle = (reader) => {
    setSelectedReaders(prev => {
      const isSelected = prev.some(r => r.id === reader.id);
      const newSelection = isSelected
        ? prev.filter(r => r.id !== reader.id)
        : [...prev, reader];
      setCurrentPage(1);
      return newSelection;
    });
  };

  const handleEditReport = (report) => {
    setEditingReport({
      id: report.id,
      notes: report.notes || '',
      readingDetails: {
        translation: report.translation || '',
        readingMethod: report.readingMethod || '',
        audioSpeed: report.audioSpeed || '',
        readingDuration: report.readingDuration || '',
        comprehensionLevel: report.comprehensionLevel || '',
        emotionalImpact: report.emotionalImpact || '',
        studyAids: report.studyAids || [],
        location: report.location || '',
        timeOfDay: report.timeOfDay || '',
        distractions: report.distractions || '',
        prayerTime: report.prayerTime || false,
        journaling: report.journaling || false
      }
    });
    setShowReadingDetails({ [report.id]: true });
  };

  const handleSaveReport = async () => {
    if (!editingReport) return;

    try {
      const updateData = {
        notes: editingReport.notes,
        ...editingReport.readingDetails
      };

      await reportApi.updateReport(editingReport.id, updateData);
      setSuccessMessage('Report updated successfully');
      setError(null);
      setEditingReport(null);
      setShowReadingDetails({});
      loadReports(); // Reload the current page
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Failed to update report:', err);
      setError(err.response?.data?.error || 'Failed to update report. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditingReport(null);
    setShowReadingDetails({});
  };

  const handleDeleteReport = async (reportId) => {
    if (!confirm('Are you sure you want to delete this reading report?')) {
      return;
    }

    try {
      await reportApi.deleteReport(reportId);
      setSuccessMessage('Report deleted successfully');
      setError(null);
      loadReports(); // Reload the current page
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Failed to delete report:', err);
      setError(err.response?.data?.error || 'Failed to delete report. Please try again.');
    }
  };

  const handleReportSelect = (reportId) => {
    setSelectedReports(prev => {
      if (prev.includes(reportId)) {
        return prev.filter(id => id !== reportId);
      } else {
        return [...prev, reportId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedReports([]);
      setSelectAll(false);
    } else {
      const currentPageReportIds = reports.map(report => report.id);
      setSelectedReports(currentPageReportIds);
      setSelectAll(true);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedReports.length === 0) {
      alert('Please select reports to delete.');
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${selectedReports.length} selected report${selectedReports.length > 1 ? 's' : ''}? This action cannot be undone.`;
    
    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      setLoadingReports(true);
      
      // Process deletions in batches to avoid timeout
      const batchSize = 5;
      const batches = [];
      for (let i = 0; i < selectedReports.length; i += batchSize) {
        batches.push(selectedReports.slice(i, i + batchSize));
      }

      let deletedCount = 0;
      let failedCount = 0;

      // Process each batch sequentially
      for (const batch of batches) {
        try {
          await Promise.all(
            batch.map(reportId => reportApi.deleteReport(reportId))
          );
          deletedCount += batch.length;
        } catch (batchError) {
          console.error('Failed to delete batch:', batchError);
          failedCount += batch.length;
          
          // Try individual deletions for failed batch
          for (const reportId of batch) {
            try {
              await reportApi.deleteReport(reportId);
              deletedCount++;
              failedCount--;
            } catch (individualError) {
              console.error(`Failed to delete report ${reportId}:`, individualError);
            }
          }
        }
      }
      
      // Show results
      if (deletedCount > 0) {
        setSuccessMessage(`${deletedCount} report${deletedCount > 1 ? 's' : ''} deleted successfully${failedCount > 0 ? `. ${failedCount} failed to delete.` : ''}`);
      }
      
      if (failedCount > 0 && deletedCount === 0) {
        setError(`Failed to delete ${failedCount} report${failedCount > 1 ? 's' : ''}. Please try again.`);
      } else {
        setError(null);
      }
      
      setSelectedReports([]);
      setSelectAll(false);
      loadReports(); // Reload the current page
      
      // Clear success message after 5 seconds (longer for bulk operations)
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Failed to delete reports:', err);
      setError(err.response?.data?.error || 'Failed to delete reports. Please try again.');
    } finally {
      setLoadingReports(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getUniqueBooks = () => {
    const books = new Set();
    reports.forEach(report => {
      if (report.chapter?.book) {
        books.add(report.chapter.book);
      }
    });
    return Array.from(books).sort();
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedBook('');
    setSelectedTestament('');
    setSelectedReports([]);
    setSelectAll(false);
    
    // For users with simplified setup, default to primary reader mode
    const isSimplifiedSetup = hasSimplifiedSetup(readers, families);
    if (isSimplifiedSetup) {
      const primaryReader = readers.find(r => r.isPrimary);
      if (primaryReader) {
        setReaderMode('primary');
        setSelectedReaders([primaryReader]);
      }
    } else {
      setReaderMode('all');
      setSelectedReaders([]);
    }
    
    setSelectedFamily(null);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="manage-reports-page loading">
        <LoadingSpinner size="large" message="Loading reports..." />
      </div>
    );
  }

  return (
    <div className="manage-reports-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="header-text">
            <h1>Reports</h1>
            <p>
              {hasSimplifiedSetup(readers, families) 
                ? "View and edit what you've logged" 
                : "View and edit logs for you and your family"
              }
            </p>
          </div>
          {competitiveMode && (
            <div className="header-actions">
              <a href="/progress#points" className="points-link-btn">
                View my points
              </a>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          <p>{successMessage}</p>
        </div>
      )}

      {/* Filters Section */}
      <div className={`filters-section ${filtersExpanded ? 'expanded' : 'collapsed'}`}>
        <div className="filters-header">
          <button 
            className="filters-toggle-btn" 
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            aria-expanded={filtersExpanded}
          >
            <span className="filters-toggle-icon">
              {filtersExpanded ? '▼' : '▶'}
            </span>
            <span>Filters</span>
            {!filtersExpanded && (startDate || endDate || selectedBook || selectedTestament || (selectedReaders.length > 0 && readerMode !== 'all')) && (
              <span className="active-filters-indicator">●</span>
            )}
          </button>
          {filtersExpanded && (
            <button className="clear-filters-btn" onClick={clearFilters}>
              Clear All Filters
            </button>
          )}
        </div>

        {filtersExpanded && (
          <div className="filters-content">
            {/* Reader Selection - Only show if user has additional readers or family */}
            {(() => {
              const isSimplifiedSetup = hasSimplifiedSetup(readers, families);
              return !isSimplifiedSetup ? (
                <div className="filter-group">
                  <h3>Readers</h3>
                  <ReaderSelector
                    readers={readers}
                    families={families}
                    readerMode={readerMode}
                    selectedReaders={selectedReaders}
                    selectedFamily={selectedFamily}
                    onReaderModeChange={handleReaderModeChange}
                    onFamilySelect={handleFamilySelect}
                    onReaderToggle={handleReaderToggle}
                    showAllOption={true}
                  />
                </div>
              ) : null;
            })()}

            {/* Date Filters */}
            <div className="filter-group">
              <h3>Date Range</h3>
              <div className="date-filters">
                <div className="form-group">
                  <label htmlFor="startDate">From:</label>
                  <input
                    type="date"
                    id="startDate"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="endDate">To:</label>
                  <input
                    type="date"
                    id="endDate"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setCurrentPage(1);
                    }}
                    max={localDateOnly()}
                  />
                </div>
              </div>
            </div>

            {/* Content Filters */}
            <div className="filter-group">
              <h3>Content</h3>
              <div className="content-filters">
                <div className="form-group">
                  <label htmlFor="testament">Testament:</label>
                  <select
                    id="testament"
                    value={selectedTestament}
                    onChange={(e) => {
                      setSelectedTestament(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">All Testaments</option>
                    <option value="Old">Old Testament</option>
                    <option value="New">New Testament</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="book">Book:</label>
                  <select
                    id="book"
                    value={selectedBook}
                    onChange={(e) => {
                      setSelectedBook(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">All Books</option>
                    {getUniqueBooks().map(book => (
                      <option key={book} value={book}>{book}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Reports Table */}
      <div className="reports-section">
        <div className="reports-header">
          <h2>Reports ({totalReports})</h2>
          <div className="reports-actions">
            {selectedReports.length > 0 && (
              <button
                className="bulk-delete-btn"
                onClick={handleBulkDelete}
                title={`Delete ${selectedReports.length} selected report${selectedReports.length > 1 ? 's' : ''}`}
              >
                🗑️ Delete Selected ({selectedReports.length})
              </button>
            )}
            {loadingReports && <div className="loading-indicator">Loading...</div>}
          </div>
        </div>

        {reports.length === 0 && !loadingReports ? (
          <div className="no-reports">
            <p>No reports found matching your filters.</p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="reports-table-container">
              {/* Table Key */}
              <div className="table-key">
                <span className="key-item">📝 = Edit</span>
                <span className="key-item">🗑️ = Delete</span>
              </div>
              
              <table className="reports-table">
                <thead>
                  <tr>
                    <th className="select-column">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        title="Select all reports"
                      />
                    </th>
                    <th>Chapter</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map(report => (
                    <React.Fragment key={report.id}>
                      <tr>
                        <td className="select-cell">
                          <input
                            type="checkbox"
                            checked={selectedReports.includes(report.id)}
                            onChange={() => handleReportSelect(report.id)}
                            disabled={editingReport?.id === report.id}
                          />
                        </td>
                        <td className="report-details-cell">
                          <div className="report-main-line">
                            <span className="reading-reference">
                              {report.chapter?.book} {report.chapter?.chapter}
                            </span>
                            <span className="report-date">{formatDate(report.reportedAt)}</span>
                          </div>
                          <div className="report-secondary-line">
                            <span className="reader-name">{report.reader?.name}</span>
                            <div className="report-actions">
                              {editingReport?.id === report.id ? (
                                <>
                                  <button
                                    className="save-btn"
                                    onClick={handleSaveReport}
                                    title="Save changes"
                                  >
                                    💾
                                  </button>
                                  <button
                                    className="cancel-btn"
                                    onClick={handleCancelEdit}
                                    title="Cancel editing"
                                  >
                                    ✕
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    className="edit-btn"
                                    onClick={() => handleEditReport(report)}
                                    title="Edit this report"
                                  >
                                    📝
                                  </button>
                                  <button
                                    className="delete-btn"
                                    onClick={() => handleDeleteReport(report.id)}
                                    title="Delete this report"
                                  >
                                    🗑️
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                      {editingReport?.id === report.id && (
                        <tr className="edit-details-row">
                          <td colSpan="2">
                            <div className="edit-form-container">
                              <div className="notes-section">
                                <label htmlFor="edit-notes">Notes:</label>
                                <textarea
                                  id="edit-notes"
                                  value={editingReport.notes}
                                  onChange={(e) => setEditingReport({
                                    ...editingReport,
                                    notes: e.target.value
                                  })}
                                  maxLength={1000}
                                  rows={3}
                                  className="edit-notes"
                                  placeholder="Add notes about this reading..."
                                />
                              </div>
                              <ReadingDetailsForm
                                formData={editingReport.readingDetails}
                                onChange={(details) => setEditingReport({
                                  ...editingReport,
                                  readingDetails: details
                                })}
                                showDetails={true}
                                onToggleDetails={() => {}}
                                compact={true}
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="reports-card-container">
              {reports.map(report => (
                <div 
                  key={report.id} 
                  className={`report-card ${selectedReports.includes(report.id) ? 'selected' : ''}`}
                >
                  <div className="report-card-header">
                    <div className="report-card-select">
                      <input
                        type="checkbox"
                        checked={selectedReports.includes(report.id)}
                        onChange={() => handleReportSelect(report.id)}
                        disabled={editingReport?.id === report.id}
                      />
                    </div>
                    <div className="report-card-main">
                      <div className="report-card-main-line">
                        <span className="reading-reference">
                          {report.chapter?.book} {report.chapter?.chapter}
                        </span>
                        <span className="report-card-date">{formatDate(report.reportedAt)}</span>
                      </div>
                      <div className="report-card-secondary-line">
                        <span className="report-card-reader">{report.reader?.name}</span>
                        {editingReport?.id !== report.id && (
                          <div className="report-card-actions">
                            <button
                              className="edit-btn"
                              onClick={() => handleEditReport(report)}
                              title="Edit this report"
                            >
                              📝
                            </button>
                            <button
                              className="delete-btn"
                              onClick={() => handleDeleteReport(report.id)}
                              title="Delete this report"
                            >
                              🗑️
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {editingReport?.id === report.id ? (
                    <div className="report-card-edit-form">
                      <div className="notes-section">
                        <label htmlFor={`mobile-edit-notes-${report.id}`}>Notes:</label>
                        <textarea
                          id={`mobile-edit-notes-${report.id}`}
                          value={editingReport.notes}
                          onChange={(e) => setEditingReport({
                            ...editingReport,
                            notes: e.target.value
                          })}
                          maxLength={1000}
                          className="report-card-edit-notes"
                          placeholder="Add notes about this reading..."
                          rows={3}
                        />
                      </div>
                      <ReadingDetailsForm
                        formData={editingReport.readingDetails}
                        onChange={(details) => setEditingReport({
                          ...editingReport,
                          readingDetails: details
                        })}
                        showDetails={true}
                        onToggleDetails={() => {}}
                        compact={true}
                      />
                      <div className="report-card-edit-actions">
                        <button
                          className="save-btn"
                          onClick={handleSaveReport}
                          title="Save changes"
                        >
                          💾 Save
                        </button>
                        <button
                          className="cancel-btn"
                          onClick={handleCancelEdit}
                          title="Cancel editing"
                        >
                          ✕ Cancel
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                
                <span className="pagination-info">
                  Page {currentPage} of {totalPages}
                </span>
                
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ManageReportsPage;
